/**
 * TES Event Helpers for Kiosk Actions
 *
 * Simplified API for emitting kiosk-specific events to TES.
 * These wrap the TES GraphQL client with kiosk-friendly semantics.
 * Authentication is handled server-side via API key proxy.
 */

import { createThing, addThingStatus, type ThingInput, type MutationResult } from './tes-client';

// =============================================================================
// TYPES
// =============================================================================

export interface DetectedItem {
  /** Unique ID for this item (generated by kiosk) */
  id: string;
  /** Product reference (SKU, barcode, etc.) */
  productRef?: string;
  /** Human-readable name */
  name?: string;
  /** Item type/category */
  type?: string;
  /** Brand that owns this product */
  brand?: string;
  /** Detection confidence (0-1) */
  confidence?: number;
  /** Quantity of items */
  quantity?: number;
  /** Additional data */
  data?: Record<string, unknown>;
}

export interface KioskContext {
  /** Kiosk/location identifier */
  kioskId?: string;
  /** Session ID for this return session */
  sessionId?: string;
  /** Brand configuration ID */
  brandId?: string;
  /** Optional holder/user ID */
  holderId?: string;
  /** Optional holder email */
  holderEmail?: string;
}

// =============================================================================
// EVENT EMISSION HELPERS
// =============================================================================

/**
 * Emit event when an item is first detected/captured by the kiosk
 *
 * Stage: captured
 * Typical trigger: Camera detects item placed on scale
 */
export async function emitItemCaptured(
  item: DetectedItem,
  context: KioskContext = {}
): Promise<MutationResult> {
  const input: ThingInput = {
    id: item.id,
    name: item.name,
    product_ref: item.productRef,
    quantity: item.quantity || 1,
    data: {
      type: item.type, // Store type in data since ThingInput doesn't have type field
      brand: item.brand,
      confidence: item.confidence,
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      ...item.data,
    },
    statuses: [
      {
        parent_status: 'captured',
        status: 'detected',
        data: {
          source: 'pin_kiosk',
          confidence: item.confidence,
        },
      },
    ],
    // Link to holder if provided in context
    ...(context.holderId && {
      holder: {
        id: context.holderId,
        type: 'customer',
        email: context.holderEmail,
      },
    }),
  };

  return createThing(input);
}

/**
 * Emit event when an item is identified (product matched)
 *
 * Stage: identified
 * Typical trigger: Vision model identifies product SKU
 */
export async function emitItemIdentified(
  thingId: string,
  identification: {
    productRef: string;
    name?: string;
    confidence?: number;
    matchMethod?: 'vision' | 'barcode' | 'manual';
    data?: Record<string, unknown>;
  },
  context: KioskContext = {}
): Promise<MutationResult> {
  return addThingStatus(thingId, {
    parent_status: 'identified',
    status: 'product_matched',
    data: {
      product_ref: identification.productRef,
      name: identification.name,
      confidence: identification.confidence,
      match_method: identification.matchMethod || 'vision',
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      ...identification.data,
    },
  });
}

/**
 * Emit event when an item is valued (price/points determined)
 *
 * Stage: valued
 * Typical trigger: System calculates return value
 */
export async function emitItemValued(
  thingId: string,
  valuation: {
    value: number;
    currency?: string;
    points?: number;
    valuationMethod?: string;
    data?: Record<string, unknown>;
  },
  context: KioskContext = {}
): Promise<MutationResult> {
  return addThingStatus(thingId, {
    parent_status: 'valued',
    status: 'value_calculated',
    value: valuation.value,
    data: {
      currency: valuation.currency || 'USD',
      points: valuation.points,
      valuation_method: valuation.valuationMethod,
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      ...valuation.data,
    },
  });
}

/**
 * Emit event when return is confirmed by user
 *
 * Stage: returned
 * Typical trigger: User confirms return on kiosk
 */
export async function emitReturnConfirmed(
  thingId: string,
  returnDetails: {
    rewardType?: 'points' | 'credit' | 'discount' | 'donation';
    rewardValue?: number;
    destinationEmail?: string;
    data?: Record<string, unknown>;
  } = {},
  context: KioskContext = {}
): Promise<MutationResult> {
  return addThingStatus(thingId, {
    parent_status: 'returned',
    status: 'return_confirmed',
    holder_id: context.holderId,
    holder_type: 'customer',
    data: {
      reward_type: returnDetails.rewardType,
      reward_value: returnDetails.rewardValue,
      destination_email: returnDetails.destinationEmail || context.holderEmail,
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      brand_id: context.brandId,
      ...returnDetails.data,
    },
  });
}

/**
 * Emit event when item is received (physically deposited)
 *
 * Stage: received
 * Typical trigger: Item deposited in bin
 */
export async function emitItemReceived(
  thingId: string,
  receiptDetails: {
    binId?: string;
    weight?: number;
    data?: Record<string, unknown>;
  } = {},
  context: KioskContext = {}
): Promise<MutationResult> {
  return addThingStatus(thingId, {
    parent_status: 'received',
    status: 'deposited',
    location_id: receiptDetails.binId || context.kioskId,
    data: {
      weight: receiptDetails.weight,
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      ...receiptDetails.data,
    },
  });
}

/**
 * Emit event when there's an issue with the return
 *
 * Stage: issue
 * Typical trigger: Item rejected, scan failed, etc.
 */
export async function emitReturnIssue(
  thingId: string,
  issue: {
    reason: string;
    code?: string;
    recoverable?: boolean;
    data?: Record<string, unknown>;
  },
  context: KioskContext = {}
): Promise<MutationResult> {
  return addThingStatus(thingId, {
    parent_status: 'issue',
    status: issue.code || 'return_issue',
    data: {
      reason: issue.reason,
      recoverable: issue.recoverable ?? true,
      kiosk_id: context.kioskId,
      session_id: context.sessionId,
      ...issue.data,
    },
  });
}

// =============================================================================
// BATCH OPERATIONS
// =============================================================================

/**
 * Emit captured events for multiple items
 */
export async function emitBatchCaptured(
  items: DetectedItem[],
  context: KioskContext = {}
): Promise<MutationResult[]> {
  return Promise.all(items.map((item) => emitItemCaptured(item, context)));
}

/**
 * Emit return confirmed for multiple items
 */
export async function emitBatchReturnConfirmed(
  thingIds: string[],
  returnDetails: Parameters<typeof emitReturnConfirmed>[1] = {},
  context: KioskContext = {}
): Promise<MutationResult[]> {
  return Promise.all(
    thingIds.map((id) => emitReturnConfirmed(id, returnDetails, context))
  );
}

// =============================================================================
// UTILITY
// =============================================================================

/**
 * Generate a unique thing ID for kiosk items
 * Format: pin_{kioskId}_{sessionId}_{timestamp}_{random}
 */
export function generateThingId(context: KioskContext = {}): string {
  const parts = ['pin'];
  if (context.kioskId) parts.push(context.kioskId);
  if (context.sessionId) parts.push(context.sessionId);
  parts.push(Date.now().toString(36));
  parts.push(Math.random().toString(36).substring(2, 8));
  return parts.join('_');
}
